import React, { useState, useEffect } from 'react';
import API_HOST from '../config';
import './Matrix.css'; // Import the CSS file

const Matrix = ({ tripOrderId, onApplyFilter, activeFilters, isResultReadyForMatrix }) => {
  const [matrixData, setMatrixData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Only fetch matrix data if we have an ID and the main results have started to arrive.
    if (!tripOrderId || !isResultReadyForMatrix) {
      setMatrixData([]); // Clear data if no tripOrderId or not ready
      return;
    }

    const fetchMatrixData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_HOST}/radar_api_key/triporder_matrix/${tripOrderId}`, {
          headers: {
            'content-type': 'application/json',
            'x-api-key': 'f4b4e7b0-5e6b-4b7e-8c6b-4d5e6b7b8c6b', // Hardcoded API key as per instructions
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        if (data && Array.isArray(data.matrix)) {
          setMatrixData(data.matrix);
        } else {
          console.warn('Matrix data is not in the expected format:', data);
          setMatrixData([]); // Set to empty array if format is unexpected
        }
      } catch (err) {
        console.error('Error fetching matrix data:', err);
        setError(err.message);
        setMatrixData([]); // Clear data on error
      } finally {
        setLoading(false);
      }
    };

    fetchMatrixData();
  }, [tripOrderId, isResultReadyForMatrix]);

  const formatPrice = (price) => {
    if (price === 0 || price === null || price === undefined) {
      return '-';
    }
    return `$${Math.ceil(price)}`;
  };

  // Render logic
  if (!tripOrderId) {
    return null; // Don't render anything if there's no tripOrderId
  }

  // This is handled by the refined step 4, but good to have intermediate loading state
  // if (loading) {
  //   return <p>Loading matrix...</p>;
  // }

  // if (error) {
  //  return <p>Error loading matrix: {error}</p>;
  // }

  // if (matrixData.length === 0 && !loading) {
  //   return <p>No matrix data available.</p>;
  // }

  if (loading) {
    return <div className="matrix-container matrix-loading"><p>Loading matrix...</p></div>;
  }

  if (error) {
   return <div className="matrix-container matrix-error"><p>Error loading matrix: {error}</p></div>;
  }

  if (matrixData.length === 0) {
    return <div className="matrix-container matrix-no-data"><p>No matrix data available.</p></div>;
  }

  // Prepare data for table rendering
  const airlines = matrixData.map(item => item.AirLine);
  const directPrices = matrixData.map(item => formatPrice(item.PriceDirect));
  const oneStopPrices = matrixData.map(item => formatPrice(item.PriceOneStop));
  const moreStopPrices = matrixData.map(item => formatPrice(item.PriceMoreStop));

  return (
    <div className="matrix-container">
      <h4>Price Matrix</h4>
      <div className="matrix-table-wrapper">
        <table>
          <thead>
            <tr>
              <th
                onClick={() => onApplyFilter('reset', null)}
                title="Reset all matrix filters"
                className="reset-filters-cell"
              >
                {/* Text "Airline" removed */}
              </th>
            {airlines.map((airline, index) => {
              const airlineCodeForFilter = airline.replace('+', ',');
              const isActive = activeFilters && activeFilters.airlines === airlineCodeForFilter && !activeFilters.stops; // Ensure only airline filter is active
              return (
                <th
                  key={index}
                  onClick={() => onApplyFilter('airlines', airlineCodeForFilter)}
                  className={isActive ? 'active-filter' : ''}
                  title={`Filter by airline: ${airline}`}
                >
                  {airline}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td
              onClick={() => onApplyFilter('stops', 'pricedirect')}
              className={activeFilters && activeFilters.stops === 'pricedirect' && !activeFilters.airlines ? 'active-filter' : ''}
              title="Filter by direct flights"
            >
              Direct
            </td>
            {matrixData.map((item, index) => {
              const airlineCodeForFilter = item.AirLine.replace('+', ',');
              const isActive = activeFilters && activeFilters.airlines === airlineCodeForFilter && activeFilters.stops === 'pricedirect';
              return (
                <td
                  key={index}
                  onClick={() => onApplyFilter('combined', { airlines: airlineCodeForFilter, stops: 'pricedirect' })}
                  className={isActive ? 'active-filter' : ''}
                  title={`Filter by ${item.AirLine} - Direct`}
                >
                  {formatPrice(item.PriceDirect)}
                </td>
              );
            })}
          </tr>
          <tr>
            <td
              onClick={() => onApplyFilter('stops', 'priceonestop')}
              className={activeFilters && activeFilters.stops === 'priceonestop' && !activeFilters.airlines ? 'active-filter' : ''}
              title="Filter by one stop flights"
            >
              1 Stop
            </td>
            {matrixData.map((item, index) => {
              const airlineCodeForFilter = item.AirLine.replace('+', ',');
              const isActive = activeFilters && activeFilters.airlines === airlineCodeForFilter && activeFilters.stops === 'priceonestop';
              return (
                <td
                  key={index}
                  onClick={() => onApplyFilter('combined', { airlines: airlineCodeForFilter, stops: 'priceonestop' })}
                  className={isActive ? 'active-filter' : ''}
                  title={`Filter by ${item.AirLine} - 1 Stop`}
                >
                  {formatPrice(item.PriceOneStop)}
                </td>
              );
            })}
          </tr>
          <tr>
            <td
              onClick={() => onApplyFilter('stops', 'pricemorestop')}
              className={activeFilters && activeFilters.stops === 'pricemorestop' && !activeFilters.airlines ? 'active-filter' : ''}
              title="Filter by 2+ stops flights"
            >
              2+ Stops
            </td>
            {matrixData.map((item, index) => {
              const airlineCodeForFilter = item.AirLine.replace('+', ',');
              const isActive = activeFilters && activeFilters.airlines === airlineCodeForFilter && activeFilters.stops === 'pricemorestop';
              return (
                <td
                  key={index}
                  onClick={() => onApplyFilter('combined', { airlines: airlineCodeForFilter, stops: 'pricemorestop' })}
                  className={isActive ? 'active-filter' : ''}
                  title={`Filter by ${item.AirLine} - 2+ Stops`}
                >
                  {formatPrice(item.PriceMoreStop)}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
      </div>
    </div>
  );
};

export default Matrix;
